import { useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { createPortal } from "react-dom";
import { gsap } from "gsap";

type Point = { x: number; y: number };

type MagneticCursorProps = {
    scopeRef: RefObject<HTMLElement | null>;
    active?: boolean;
    externalScopeSelector?: string;
    magneticFactor?: number;
    lerpAmount?: number;
    hoverPadding?: number;
    hoverAttribute?: string;
    cursorSize?: number;
    cursorColor?: string;
    blendMode?: CSSProperties["mixBlendMode"];
    speedMultiplier?: number;
    maxScaleX?: number;
    maxScaleY?: number;
    contrastBoost?: number;
};

type CursorState = {
    current: Point;
    target: Point;
    previous: Point;
    hovered: boolean;
    detaching: boolean;
    initialized: boolean;
};

export function MagneticCursor({
    scopeRef,
    active = true,
    externalScopeSelector,
    magneticFactor = 0.48,
    lerpAmount = 0.12,
    hoverPadding = 10,
    hoverAttribute = "data-footer-magnetic",
    cursorSize = 28,
    cursorColor = "#ffffff",
    blendMode = "exclusion",
    speedMultiplier = 0.02,
    maxScaleX = 0.9,
    maxScaleY = 0.28,
    contrastBoost = 1.5,
}: MagneticCursorProps) {
    const cursorRef = useRef<HTMLDivElement>(null);
    const [enabled, setEnabled] = useState(false);

    useEffect(() => {
        const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
        const updateCapability = () => setEnabled(finePointer.matches);

        updateCapability();
        finePointer.addEventListener("change", updateCapability);
        return () => finePointer.removeEventListener("change", updateCapability);
    }, []);

    useEffect(() => {
        const scope = scopeRef.current;
        const cursor = cursorRef.current;
        if (!active || !enabled || !scope || !cursor) return;

        const externalScopes = externalScopeSelector
            ? Array.from(document.querySelectorAll<HTMLElement>(externalScopeSelector))
            : [];
        const scopes = [scope, ...externalScopes];

        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const effectiveLerp = reducedMotion ? 1 : lerpAmount;
        const detachDuration = reducedMotion ? 0.1 : 0.35;
        const enteredScopes = new Set<HTMLElement>();
        const state: CursorState = {
            current: { x: -100, y: -100 },
            target: { x: -100, y: -100 },
            previous: { x: -100, y: -100 },
            hovered: false,
            detaching: false,
            initialized: false,
        };

        const resetCursorGeometry = () => {
            gsap.killTweensOf(cursor);
            gsap.set(cursor, {
                backgroundColor: cursorColor,
                borderRadius: "50%",
                height: cursorSize,
                opacity: 0,
                rotate: 0,
                scaleX: 1,
                scaleY: 1,
                width: cursorSize,
                x: -100,
                xPercent: -50,
                y: -100,
                yPercent: -50,
            });
        };

        resetCursorGeometry();

        const initializePosition = (event: PointerEvent) => {
            state.current.x = event.clientX;
            state.current.y = event.clientY;
            state.target.x = event.clientX;
            state.target.y = event.clientY;
            state.previous.x = event.clientX;
            state.previous.y = event.clientY;
            state.initialized = true;
            gsap.set(cursor, { x: event.clientX, y: event.clientY });
        };

        const update = () => {
            if (!state.initialized || state.hovered) return;

            state.current.x += (state.target.x - state.current.x) * effectiveLerp;
            state.current.y += (state.target.y - state.current.y) * effectiveLerp;

            const deltaX = state.current.x - state.previous.x;
            const deltaY = state.current.y - state.previous.y;
            state.previous.x = state.current.x;
            state.previous.y = state.current.y;

            if (state.detaching) {
                gsap.set(cursor, {
                    rotate: 0,
                    scaleX: 1,
                    scaleY: 1,
                    x: state.current.x,
                    y: state.current.y,
                });
                return;
            }

            const speed = Math.hypot(deltaX, deltaY) * speedMultiplier;
            gsap.set(cursor, {
                rotate: Math.atan2(deltaY, deltaX) * (180 / Math.PI),
                scaleX: 1 + Math.min(speed, maxScaleX),
                scaleY: 1 - Math.min(speed, maxScaleY),
                x: state.current.x,
                y: state.current.y,
            });
        };

        const revealCursor = (event: PointerEvent) => {
            cursor.style.willChange = "transform, width, height, border-radius";
            if (!state.initialized) initializePosition(event);
            gsap.killTweensOf(cursor, "opacity");
            gsap.set(cursor, { backgroundColor: cursorColor, opacity: 1 });
        };

        const handleScopeEnter = (event: PointerEvent) => {
            enteredScopes.add(event.currentTarget as HTMLElement);
            revealCursor(event);
        };

        const handleScopeMove = (event: PointerEvent) => {
            const cursorScope = event.currentTarget as HTMLElement;
            if (!enteredScopes.has(cursorScope)) {
                enteredScopes.add(cursorScope);
                revealCursor(event);
            }
            if (!state.initialized) initializePosition(event);
            state.target.x = event.clientX;
            state.target.y = event.clientY;
        };

        const handleScopeLeave = (event: PointerEvent) => {
            enteredScopes.delete(event.currentTarget as HTMLElement);
            if (enteredScopes.size > 0) return;
            gsap.to(cursor, {
                duration: 0.24,
                opacity: 0,
                overwrite: "auto",
                onComplete: () => {
                    if (enteredScopes.size === 0) cursor.style.willChange = "auto";
                },
            });
        };

        const cleanupElements: Array<() => void> = [];
        const magneticElements = Array.from(new Set(
            scopes.flatMap((cursorScope) =>
                Array.from(cursorScope.querySelectorAll<HTMLElement>(`[${hoverAttribute}]`)),
            ),
        ));

        magneticElements.forEach((element) => {
            const xTo = gsap.quickTo(element, "x", { duration: 1, ease: "elastic.out(1, 0.3)" });
            const yTo = gsap.quickTo(element, "y", { duration: 1, ease: "elastic.out(1, 0.3)" });
            let frame = 0;

            const handlePointerEnter = (event: PointerEvent) => {
                const bounds = element.getBoundingClientRect();
                const computedStyle = window.getComputedStyle(element);
                const dynamicPadding = hoverPadding * (1 + magneticFactor);
                const centerX = bounds.left + bounds.width / 2;
                const centerY = bounds.top + bounds.height / 2;
                const magneticColor = element.getAttribute("data-magnetic-color") || cursorColor;

                state.hovered = true;
                state.detaching = false;
                element.dataset.magneticActive = "true";
                cursor.style.willChange = "transform, width, height, border-radius";
                if (!state.initialized) initializePosition(event);

                gsap.killTweensOf(cursor);
                gsap.set(cursor, { backgroundColor: magneticColor, opacity: 1 });
                gsap.to(cursor, {
                    borderRadius: computedStyle.borderRadius,
                    duration: reducedMotion ? 0.1 : 0.3,
                    ease: "power3.out",
                    height: bounds.height + dynamicPadding * 2,
                    rotate: 0,
                    scaleX: 1,
                    scaleY: 1,
                    width: bounds.width + dynamicPadding * 2,
                    x: centerX,
                    y: centerY,
                    overwrite: "auto",
                });
            };

            const handlePointerMove = (event: PointerEvent) => {
                state.target.x = event.clientX;
                state.target.y = event.clientY;
                if (frame) return;

                frame = window.requestAnimationFrame(() => {
                    const bounds = element.getBoundingClientRect();
                    xTo((event.clientX - (bounds.left + bounds.width / 2)) * magneticFactor);
                    yTo((event.clientY - (bounds.top + bounds.height / 2)) * magneticFactor);
                    frame = 0;
                });
            };

            const handlePointerLeave = () => {
                const cursorX = Number(gsap.getProperty(cursor, "x"));
                const cursorY = Number(gsap.getProperty(cursor, "y"));

                state.current.x = cursorX;
                state.current.y = cursorY;
                state.previous.x = cursorX;
                state.previous.y = cursorY;
                state.hovered = false;
                state.detaching = true;
                delete element.dataset.magneticActive;
                xTo(0);
                yTo(0);

                gsap.killTweensOf(cursor);
                gsap.to(cursor, {
                    backgroundColor: cursorColor,
                    borderRadius: "50%",
                    duration: detachDuration,
                    ease: "power3.out",
                    height: cursorSize,
                    scaleX: 1,
                    scaleY: 1,
                    width: cursorSize,
                    overwrite: "auto",
                    onComplete: () => {
                        state.detaching = false;
                    },
                });
            };

            element.addEventListener("pointerenter", handlePointerEnter);
            element.addEventListener("pointermove", handlePointerMove, { passive: true });
            element.addEventListener("pointerleave", handlePointerLeave);

            cleanupElements.push(() => {
                window.cancelAnimationFrame(frame);
                element.removeEventListener("pointerenter", handlePointerEnter);
                element.removeEventListener("pointermove", handlePointerMove);
                element.removeEventListener("pointerleave", handlePointerLeave);
                delete element.dataset.magneticActive;
                gsap.set(element, { clearProps: "transform" });
            });
        });

        gsap.ticker.add(update);
        scopes.forEach((cursorScope) => {
            cursorScope.dataset.magneticCursorScope = "true";
        });
        scopes.forEach((cursorScope) => {
            cursorScope.addEventListener("pointerenter", handleScopeEnter);
            cursorScope.addEventListener("pointermove", handleScopeMove, { passive: true });
            cursorScope.addEventListener("pointerleave", handleScopeLeave);
        });

        return () => {
            gsap.ticker.remove(update);
            scopes.forEach((cursorScope) => {
                cursorScope.removeEventListener("pointerenter", handleScopeEnter);
                cursorScope.removeEventListener("pointermove", handleScopeMove);
                cursorScope.removeEventListener("pointerleave", handleScopeLeave);
            });
            scopes.forEach((cursorScope) => {
                delete cursorScope.dataset.magneticCursorScope;
            });
            cleanupElements.forEach((cleanup) => cleanup());
            resetCursorGeometry();
            cursor.style.willChange = "auto";
        };
    }, [
        active,
        cursorColor,
        cursorSize,
        enabled,
        externalScopeSelector,
        hoverAttribute,
        hoverPadding,
        lerpAmount,
        magneticFactor,
        maxScaleX,
        maxScaleY,
        scopeRef,
        speedMultiplier,
    ]);

    const style: CSSProperties = {
        WebkitBackdropFilter: contrastBoost === 1 ? undefined : `contrast(${contrastBoost})`,
        backdropFilter: contrastBoost === 1 ? undefined : `contrast(${contrastBoost})`,
        backgroundColor: cursorColor,
        borderRadius: "50%",
        display: active && enabled ? "block" : "none",
        height: cursorSize,
        mixBlendMode: blendMode,
        width: cursorSize,
    };

    if (typeof document === "undefined") return null;

    return createPortal(
        <div aria-hidden="true" className="magnetic-cursor" ref={cursorRef} style={style} />,
        document.body,
    );
}
